import React, { useCallback, useMemo, useState } from 'react';
import { useSignal } from '@preact/signals-react';
import { useSelector } from 'react-redux';
import { FormProvider, type FieldErrors } from 'react-hook-form';
import makeStyles from '@mui/styles/makeStyles';
import { Tab, Tabs, Button, DialogContent, DialogActions, DialogTitle, Dialog, Badge, CircularProgress, Stack, Typography } from '@mui/material';

import TabPanel from 'app/components/ScenarioEditorPage/ScenarioSettingsDialog/TabPanel.tsx';
import useScenarioForm, { type ScenarioSettingsType } from 'app/components/ScenarioEditorPage/ScenarioSettingsDialog/hooks/useScenarioForm.ts';
import type { ScenarioSettingsComponentProps } from 'app/components/ScenarioEditorPage/ScenarioSettingsDialog/types.ts';
import ScenarioContextSettings from 'app/components/ScenarioEditorPage/ScenarioSettingsDialog/ScenarioContextSettings.tsx';
import { ScenarioGeneralSettings } from 'app/components/ScenarioEditorPage/ScenarioSettingsDialog/ScenarioGeneralSettings/index.ts';
import ScenarioAdvancedSettings from 'app/components/ScenarioEditorPage/ScenarioSettingsDialog/ScenarioAdvancedSettings/index.js';
import ScenarioIntroAudioSettingsTab from 'app/components/ScenarioEditorPage/ScenarioSettingsDialog/ScenarioIntroAudioSettings/ScenarioIntroAudioSettingsTab.tsx';
import ScenarioVariablesSettings from 'app/components/ScenarioEditorPage/ScenarioSettingsDialog/ScenarioVariablesSettings/index.ts';
import ScenarioEvaluationProtocolsSettings from 'app/components/ScenarioEditorPage/ScenarioSettingsDialog/ScenarioEvaluationProtocolsSettings/ScenarioEvaluationProtocolsSettings.tsx';
import ScenarioPersonasSettings from 'app/components/ScenarioEditorPage/ScenarioSettingsDialog/ScenarioPersonasSettings';
import PersonaSelectionDialog from 'app/components/ScenarioEditorPage/ScenarioSettingsDialog/ScenarioPersonasSettings/PersonaSelectionDialog';
import type { PersonaDto, PersonaUsage } from '@/api-gen/api.schemas';
import { useScenarioPersonas } from 'app/components/ScenarioEditorPage/ScenarioSettingsDialog/ScenarioPersonasSettings/hooks/useScenarioPersonas';
import { ErrorBoundary } from 'app/components/ErrorBoundary';
import { ScenarioVariablesFormProvider } from 'app/components/ScenarioEditorPage/ScenarioSettingsDialog/ScenarioVariablesSettings/ScenarioVariablesFormContext.tsx';

import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/i18n/types/i18nNamespace.ts';
import type { I18nCommonNs, I18nScenarioEditorNs } from '@/i18n/dictionaries/interfaces.ts';
import { CurrentUserSelector, isUserAdminSelector } from 'app/selectors/userSelectors.ts';
import * as ScenarioSelectors from 'app/selectors/scenarioSelectors.ts';

type TabId = 'general' | 'aiContext' | 'introAudio' | 'variables' | 'evaluationProtocols' | 'personas' | 'advanced';

type TabDef = {
  id: TabId;
  label: string;
  dataCy?: string;
};

const useStyles = makeStyles(() => {
  return {
    actions: {
      marginTop: 'auto'
    }
  };
});

const ScenarioSettingsDialog: React.FC<ScenarioSettingsComponentProps> = (props) => {
  const { open, onClose, scenarioId } = props;
  const [translate] = useTranslation([I18nNamespace.ScenarioEditor]);
  const [translateCommon] = useTranslation([I18nNamespace.Common]);
  const isAdmin = useSelector(isUserAdminSelector);
  const currentUser = useSelector(CurrentUserSelector);
  const scenario = useSelector(ScenarioSelectors.getScenarioById(scenarioId ?? -1));

  const allTabs: TabDef[] = [
    { id: 'general', label: translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioSettingsDialog.general)), dataCy: 'generalTab' },
    { id: 'aiContext', label: translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioSettingsDialog.aiContext)) },
    { id: 'introAudio', label: translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioSettingsDialog.introAudio)) },
    { id: 'variables', label: translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioActionsSection.variables)) },
    { id: 'evaluationProtocols', label: translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioSettingsDialog.evaluationProtocols)), dataCy: 'evaluationProtocolsTab' },
    { id: 'personas', label: translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.title)), dataCy: 'personasTab' },
    { id: 'advanced', label: translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioSettingsDialog.advanced)), dataCy: 'advancedTab' },
  ];

  const isSharedScenarioForAdmin =
    isAdmin && scenarioId != null && scenario != null && scenario.companyId !== currentUser?.companyId;

  // Keep default tab visibility.
  // For shared scenarios, admins get read-only access outside Variables.
  const tabs = useMemo(() => allTabs, [allTabs]);

  const selectedTab = useSignal<TabId>('general');
  const visibleTabIds = new Set(tabs.map((t) => t.id));
  const activeTab = visibleTabIds.has(selectedTab.value) ? selectedTab.value : tabs[0]?.id;
  const setActiveTab = useCallback((id: TabId) => { selectedTab.value = id; }, [selectedTab]);

  const [isPersonaSelectionDialogOpen, setIsPersonaSelectionDialogOpen] = useState(false);

  // Find the first tab with validation errors and navigate to it
  const handleValidationError = useCallback((errors: FieldErrors<ScenarioSettingsType>) => {
    const errorToTabMap: Array<{ tabId: TabId; hasError: () => boolean }> = [
      { tabId: 'personas', hasError: () => !!errors.scenarioPersonas },
      { tabId: 'general', hasError: () => !!errors.name },
      { tabId: 'aiContext', hasError: () => !!errors.context }
    ];

    for (const { tabId, hasError } of errorToTabMap) {
      if (hasError() && visibleTabIds.has(tabId)) {
        setActiveTab(tabId);
        return;
      }
    }
  }, [visibleTabIds]);

  const { methods, onSubmit, isScenarioLoadedForSubmit } = useScenarioForm(onClose, scenarioId, handleValidationError);
  const isScenarioLoadingForSubmit = !isScenarioLoadedForSubmit;
  const isReadOnlyOutsideVariables = isSharedScenarioForAdmin;
  const readOnlySectionFieldsetStyle = {
    border: 0,
    margin: 0,
    padding: 0,
    minWidth: 0,
    ...(isReadOnlyOutsideVariables
      ? {
          opacity: 0.6,
          pointerEvents: 'none' as const
        }
      : {})
  };

  // Watch current scenario personas for persona dialog
  const scenarioPersonas = methods.watch('scenarioPersonas') || [];
  
  // Shared persona operations 
  const { addPersona, removePersona, changeUsages } = useScenarioPersonas(scenarioPersonas, methods.setValue, methods.trigger, translate);

  const handlePersonaDialogOpen = () => {
    setIsPersonaSelectionDialogOpen(true);
  };

  const handlePersonaDialogClose = () => {
    setIsPersonaSelectionDialogOpen(false);
  };

  const handlePersonaSelect = (persona: PersonaDto, usages: PersonaUsage[]) => {
    // In the outer dialog we don't have roles context; pass undefined and let
    // per-tab logic or selectors enforce defaults if needed.
    const result = addPersona(persona, usages, undefined);
    if (!result.success) {
      return;
    }
    setIsPersonaSelectionDialogOpen(false);
  };

  const existingPersonaIds = scenarioPersonas
    .map(sp => sp.personaId)
    .filter((id): id is number => id != null);

  const getTabHasError = (tabId: TabId): boolean => {
    const errors = methods.formState.errors;
    if (tabId === 'general') return !!errors.name;
    if (tabId === 'aiContext') return !!errors.context;
    if (tabId === 'personas') return !!errors.scenarioPersonas;
    return false;
  };

  const classes = useStyles();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="form-dialog-title"
      data-cy="scenarioSettingForm"
      maxWidth="lg"
      fullWidth
      TransitionProps={{
        onExited: () => { if (tabs.length > 0) setActiveTab(tabs[0].id); }
      }}
    >
      <DialogTitle id="form-dialog-title">
        {translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioSettingsDialog.scenarioSettings))}
      </DialogTitle>

      <DialogContent sx={{minHeight: '60vh', maxHeight: '80vh'}}>
        {isScenarioLoadingForSubmit && (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="body2">
              {translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioSettingsDialog.loading))}
            </Typography>
          </Stack>
        )}

        <FormProvider {...methods}>
          <ScenarioVariablesFormProvider>
            <form onSubmit={onSubmit} autoComplete="off" id="scenario-settings">
            <Tabs
              value={activeTab ?? false}
              onChange={(_event, value: TabId) => {
                setActiveTab(value);
              }}
            >
              {tabs.map((tab) => {
                const hasError = getTabHasError(tab.id);
                const label = hasError ? (
                  <Badge
                    color="warning"
                    variant="dot"
                    sx={{
                      '& .MuiBadge-badge': {
                        backgroundColor: '#ff9800',
                        top: -8,
                        right: -8
                      }
                    }}
                  >
                    {tab.label}
                  </Badge>
                ) : tab.label;

                return (
                  <Tab
                    key={tab.id}
                    label={label}
                    value={tab.id}
                    data-cy={tab.dataCy}
                  />
                );
              })}
            </Tabs>

            {visibleTabIds.has('general') && (
              <TabPanel index="general" value={activeTab}>
                <fieldset disabled={isReadOnlyOutsideVariables} style={readOnlySectionFieldsetStyle}>
                  <ScenarioGeneralSettings onClose={onClose} scenarioId={scenarioId} />
                </fieldset>
              </TabPanel>
            )}

            {visibleTabIds.has('aiContext') && (
              <TabPanel index="aiContext" value={activeTab}>
                <fieldset disabled={isReadOnlyOutsideVariables} style={readOnlySectionFieldsetStyle}>
                  <ScenarioContextSettings scenarioId={scenarioId} />
                </fieldset>
              </TabPanel>
            )}

            {visibleTabIds.has('introAudio') && (
              <fieldset disabled={isReadOnlyOutsideVariables} style={readOnlySectionFieldsetStyle}>
                <ScenarioIntroAudioSettingsTab index="introAudio" value={activeTab} />
              </fieldset>
            )}

            {visibleTabIds.has('variables') && (
              <TabPanel index="variables" value={activeTab}>
                <ScenarioVariablesSettings />
              </TabPanel>
            )}

            {visibleTabIds.has('evaluationProtocols') && (
              <TabPanel index="evaluationProtocols" value={activeTab}>
                <fieldset disabled={isReadOnlyOutsideVariables} style={readOnlySectionFieldsetStyle}>
                  <ScenarioEvaluationProtocolsSettings scenarioId={scenarioId} />
                </fieldset>
              </TabPanel>
            )}

            {visibleTabIds.has('personas') && (
              <TabPanel index="personas" value={activeTab}>
                <fieldset disabled={isReadOnlyOutsideVariables} style={readOnlySectionFieldsetStyle}>
                  <ErrorBoundary>
                    <ScenarioPersonasSettings />
                  </ErrorBoundary>
                </fieldset>
              </TabPanel>
            )}

            {visibleTabIds.has('advanced') && (
              <TabPanel index="advanced" value={activeTab}>
                <fieldset disabled={isReadOnlyOutsideVariables} style={readOnlySectionFieldsetStyle}>
                  <ScenarioAdvancedSettings onClose={onClose} scenarioId={scenarioId} />
                </fieldset>
              </TabPanel>
            )}
          </form>
          </ScenarioVariablesFormProvider>
        </FormProvider>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button size="medium" onClick={onClose}>
          {translateCommon(nameof.full<I18nCommonNs>((n) => n.buttonLabels.cancel))}
        </Button>
        <Button
          variant="contained"
          data-cy="submitCreatedScenarioButton"
          color="primary"
          type="submit"
          form="scenario-settings"
          disabled={isScenarioLoadingForSubmit}
        >
          {translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioSettingsDialog.save))}
        </Button>
      </DialogActions>
      
      {/* Persona Selection Dialog - Outside the form to prevent event bubbling */}
      <PersonaSelectionDialog
        open={isPersonaSelectionDialogOpen}
        onClose={handlePersonaDialogClose}
        onPersonaSelect={handlePersonaSelect}
        existingPersonaIds={existingPersonaIds}
      />
    </Dialog>
  );
};

export default ScenarioSettingsDialog;
